import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { ReactNode, useId } from 'react';

import {
  ComposerAttachmentConfig,
  MAX_ATTACHMENT_SIZE_IN_MB,
  MAX_ATTACHMENTS_PER_MESSAGE,
} from '~hooks/useComposerAttachments/composerAttachmentConfigs';

// Long enough that the hint does not flash while the pointer passes over the button.
const OPEN_DELAY_IN_MS = 300;

interface AttachButtonHintProps {
  attachmentCount: number;
  children: (hintId: string) => ReactNode;
  config: ComposerAttachmentConfig;
  isDisabled?: boolean;
}

export default function AttachButtonHint({
  attachmentCount,
  children,
  config,
  isDisabled = false,
}: AttachButtonHintProps) {
  const hintId = useId();

  const isAtAttachmentLimit = attachmentCount >= MAX_ATTACHMENTS_PER_MESSAGE;
  const isOverAttachmentLimit = attachmentCount > MAX_ATTACHMENTS_PER_MESSAGE;
  const isAnchorFocusable = isAtAttachmentLimit && !isDisabled;

  let mainLine = t(
    config.supportsPaste
      ? 'attachments.hint_click_drag_or_paste'
      : 'attachments.hint_click_or_drag',
  );

  if (isOverAttachmentLimit) {
    mainLine = t('attachments.hint_over_limit', {
      attached: attachmentCount,
      excess: attachmentCount - MAX_ATTACHMENTS_PER_MESSAGE,
      max: MAX_ATTACHMENTS_PER_MESSAGE,
    });
  } else if (isAtAttachmentLimit) {
    mainLine = t('attachments.hint_at_limit', {
      attached: attachmentCount,
      max: MAX_ATTACHMENTS_PER_MESSAGE,
    });
  }
  const limitsLine = t('attachments.hint_limits', {
    extensions: config.acceptedExtensions.join(', ').toUpperCase(),
    max: MAX_ATTACHMENTS_PER_MESSAGE,
    maxSize: MAX_ATTACHMENT_SIZE_IN_MB,
  });

  return (
    <Tooltip
      describeChild
      disableFocusListener={isDisabled}
      disableHoverListener={isDisabled}
      disableTouchListener={isDisabled}
      enterDelay={OPEN_DELAY_IN_MS}
      enterNextDelay={OPEN_DELAY_IN_MS}
      id={hintId}
      placement="top"
      slotProps={{
        tooltip: {
          sx: (theme) => ({
            backgroundColor: theme.semantic.surface,
            border: `1px solid ${theme.brandV2.colors.treadGray7}`,
            borderRadius: '0.25rem',
            boxShadow: theme.shadows[3],
            // The panel width RateProvenanceTooltip uses, narrowed on phones to keep a 1rem gutter each side.
            maxWidth: 'min(24rem, calc(100vw - 2rem))',
            px: 1.5,
            py: 1,
          }),
        },
      }}
      title={
        <Box data-test-id="attach-button-hint">
          <Typography
            sx={(theme) => ({
              color: theme.brandV2.colors.treadBlack,
              fontSize: '14px',
              fontWeight: 600,
            })}
          >
            {mainLine}
          </Typography>
          <Typography
            sx={(theme) => ({
              color: theme.brandV2.colors.treadGray3,
              fontSize: '12px',
              fontWeight: 600,
            })}
          >
            {limitsLine}
          </Typography>
        </Box>
      }
    >
      {/* A disabled button fires no hover or focus events and leaves the tab order, so the span keeps the hint reachable at the cap. */}
      <Box
        component="span"
        data-test-id="attach-button-hint-anchor"
        sx={{ display: 'inline-flex', flexShrink: 0 }}
        tabIndex={isAnchorFocusable ? 0 : undefined}
      >
        {children(hintId)}
      </Box>
    </Tooltip>
  );
}
